// src/lib/layouts/layoutFieldBinds.ts
import type { PostLayoutConfig, PostLayoutKind } from './types';

export type LayoutBindSource =
  | 'clipTitle'
  | 'channelName'
  | 'channelAvatar'
  | 'userName'
  | 'userAvatar';
export type LayoutFieldBinds = Partial<Record<string, LayoutBindSource>>;

export interface LayoutBindContext {
  clipTitle: string;
  channelName?: string;
  channelAvatarUrl?: string;
  userName?: string;
  userAvatarUrl?: string;
}

const CLIP_TITLE_KEYS = ['headline', 'title', 'title2', 'caption', 'postText', 'videoTitle'] as const;
const CHANNEL_NAME_KEYS = ['handle', 'profileName', 'displayName'] as const;
const CHANNEL_AVATAR_KEYS = ['avatarUrl'] as const;
const USER_NAME_KEYS = ['profileName'] as const;
const USER_AVATAR_KEYS = ['avatarUrl'] as const;

const SOURCE_KEYS: Record<LayoutBindSource, readonly string[]> = {
  clipTitle: CLIP_TITLE_KEYS,
  channelName: CHANNEL_NAME_KEYS,
  channelAvatar: CHANNEL_AVATAR_KEYS,
  userName: USER_NAME_KEYS,
  userAvatar: USER_AVATAR_KEYS,
};

export function resolveClipTitleForBind(title?: string | null): string {
  if (!title || title === 'Untitled') return '';
  return title;
}

function allowedSourcesForField(kind: PostLayoutKind, key: string): readonly LayoutBindSource[] {
  return BINDABLE_KEYS_BY_KIND[kind]?.[key] ?? [];
}

function allowsSource(kind: PostLayoutKind, key: string, source: LayoutBindSource): boolean {
  return allowedSourcesForField(kind, key).includes(source);
}

export function isKeyBindable(
  kind: PostLayoutKind,
  key: string,
  source: LayoutBindSource,
): boolean {
  if (!SOURCE_KEYS[source].includes(key)) return false;
  return allowsSource(kind, key, source);
}

const BINDABLE_KEYS_BY_KIND: Partial<Record<PostLayoutKind, Record<string, readonly LayoutBindSource[]>>> = {
  model_x: { postText: ['clipTitle'], displayName: ['channelName'], avatarUrl: ['channelAvatar'] },
  choquei: { headline: ['clipTitle'], avatarUrl: ['channelAvatar'] },
  breaking_news: { headline: ['clipTitle'] },
  newspaper: { headline: ['clipTitle'] },
  instagram: { caption: ['clipTitle'], handle: ['channelName'], avatarUrl: ['channelAvatar'] },
  fact_quote: { headline: ['clipTitle'] },
  satisfatorio: { title: ['clipTitle'] },
  triple_split: { title: ['clipTitle'], title2: ['clipTitle'] },
  news_post: { headline: ['clipTitle'], avatarUrl: ['channelAvatar'] },
  cortes: { headline: ['clipTitle'], handle: ['channelName'] },
  circulo: {},
  fofoca: {
    caption: ['clipTitle'],
    videoTitle: ['clipTitle'],
    handle: ['channelName'],
    avatarUrl: ['channelAvatar', 'userAvatar'],
    profileName: ['channelName', 'userName'],
  },
};

export function clipTitleKeysForKind(kind: PostLayoutKind): string[] {
  const allowed = BINDABLE_KEYS_BY_KIND[kind] ?? {};
  return Object.entries(allowed)
    .filter(([, sources]) => sources.includes('clipTitle'))
    .map(([key]) => key);
}

/** Default template/editor bind: use each clip's title for title-like fields. */
const SKIP_DEFAULT_CLIP_TITLE_KEYS = new Set(['videoTitle', 'title2']);

export function defaultClipTitleBinds(kind: PostLayoutKind): LayoutFieldBinds {
  const next: LayoutFieldBinds = {};
  for (const key of clipTitleKeysForKind(kind)) {
    if (SKIP_DEFAULT_CLIP_TITLE_KEYS.has(key)) continue;
    next[key] = 'clipTitle';
  }
  return next;
}

export function defaultChannelIdentityBinds(kind: PostLayoutKind): LayoutFieldBinds {
  const next: LayoutFieldBinds = {};
  const allowed = BINDABLE_KEYS_BY_KIND[kind] ?? {};
  for (const [key, sources] of Object.entries(allowed)) {
    if (sources.length === 1 && (sources[0] === 'channelName' || sources[0] === 'channelAvatar')) {
      next[key] = sources[0];
    }
  }
  return next;
}

export function defaultLayoutBinds(kind: PostLayoutKind): LayoutFieldBinds {
  return {
    ...defaultClipTitleBinds(kind),
    ...(kind === 'model_x' ? defaultChannelIdentityBinds(kind) : {}),
  };
}

/** Turn on clip-title binds for fields whose current value equals `clipTitle`.
 *  Empty titles never match. Existing binds are kept. */
export function withMatchingClipTitleBinds(
  kind: PostLayoutKind,
  layout: Record<string, unknown> | PostLayoutConfig,
  clipTitle: string,
  existing: LayoutFieldBinds = {},
): LayoutFieldBinds {
  const next: LayoutFieldBinds = { ...existing };
  if (!clipTitle) return sanitizeLayoutFieldBinds(kind, next);
  const fields = layout as Record<string, unknown>;
  for (const key of clipTitleKeysForKind(kind)) {
    if (fields[key] === clipTitle) next[key] = 'clipTitle';
  }
  return sanitizeLayoutFieldBinds(kind, next);
}

export function sanitizeLayoutFieldBinds(
  kind: PostLayoutKind,
  binds: LayoutFieldBinds,
): LayoutFieldBinds {
  const next: LayoutFieldBinds = {};
  for (const [key, source] of Object.entries(binds)) {
    if (!source) continue;
    if (allowsSource(kind, key, source)) next[key] = source;
  }
  return next;
}

export function applyLayoutBinds(
  layout: PostLayoutConfig,
  binds: LayoutFieldBinds,
  ctx: LayoutBindContext,
): PostLayoutConfig {
  const next: Record<string, unknown> = { ...layout };
  for (const [key, source] of Object.entries(binds)) {
    if (!source || !allowsSource(layout.kind, key, source)) continue;
    if (source === 'clipTitle') {
      next[key] = ctx.clipTitle;
      continue;
    }
    if (source === 'channelName') {
      if (ctx.channelName === undefined) continue;
      next[key] = ctx.channelName;
      continue;
    }
    if (source === 'channelAvatar') {
      if (ctx.channelAvatarUrl === undefined) continue;
      next[key] = ctx.channelAvatarUrl;
      continue;
    }
    if (source === 'userName') {
      if (ctx.userName === undefined) continue;
      next[key] = ctx.userName;
      continue;
    }
    if (ctx.userAvatarUrl === undefined) continue;
    next[key] = ctx.userAvatarUrl;
  }
  return next as unknown as PostLayoutConfig;
}

/** Dual-button identity sources (channel + account). Empty for clip-title/handle pills. */
export function identityBindSourcesForField(
  kind: PostLayoutKind,
  key: string,
): LayoutBindSource[] {
  const sources = allowedSourcesForField(kind, key);
  if (!sources.includes('userName') && !sources.includes('userAvatar')) return [];
  return [...sources];
}

export function bindControlForField(
  kind: PostLayoutKind,
  key: string,
  { hasChannel }: { hasChannel: boolean },
): LayoutBindSource | null {
  if (identityBindSourcesForField(kind, key).length > 0) return null;
  const sources = allowedSourcesForField(kind, key);
  if (sources.length !== 1) return null;
  const source = sources[0];
  if ((source === 'channelName' || source === 'channelAvatar') && !hasChannel) return null;
  return source;
}
